/**
 * Integrytas Group - Per-application page component map extension point.
 *
 * BaseApp ships this as an EMPTY map. Derived applications own this file and map
 * their own page `internal_code` -> React component here, instead of editing the
 * shared `src/pages/page_components.tsx`. BaseApp never modifies this file after
 * creating it, so `git merge upstream/master` stays conflict-free for derived
 * apps.
 *
 * Entries are spread into PAGE_COMPONENTS by `src/pages/page_components.tsx`, so
 * each app page is mounted/asserted by the screen smoke test automatically.
 */

import type { ComponentType } from 'react';
import type { PageComponentProps } from '../pages/page_components';
import { SfkApplicationPage } from '../pages/sfk/SfkApplicationPage';
import { SfkBundlePage } from '../pages/sfk/SfkBundlePage';
import { SfkOrgNodePage } from '../pages/sfk/SfkOrgNodePage';
import { SfkIngestionRunPage } from '../pages/sfk/SfkIngestionRunPage';
import { SfkApiFieldMapPage } from '../pages/sfk/SfkApiFieldMapPage';
import { SfkVariancePage } from '../pages/sfk/SfkVariancePage';
import { SfkIntakeRequestPage } from '../pages/sfk/SfkIntakeRequestPage';
import { SfkConflictRulePage } from '../pages/sfk/SfkConflictRulePage';
import { SfkSpofFindingPage } from '../pages/sfk/SfkSpofFindingPage';
import { SfkAppUserGroupPage } from '../pages/sfk/SfkAppUserGroupPage';
import { SfkViolationPage } from '../pages/sfk/SfkViolationPage';
import { SfkMitigationTemplatePage } from '../pages/sfk/SfkMitigationTemplatePage';
import { SfkSandboxSessionPage } from '../pages/sfk/SfkSandboxSessionPage';
import { SfkDelegationPage } from '../pages/sfk/SfkDelegationPage';
import { SfkOpenTransactionPage } from '../pages/sfk/SfkOpenTransactionPage';
import { SfkAlertPage } from '../pages/sfk/SfkAlertPage';
import { SfkReleasePage } from '../pages/sfk/SfkReleasePage';
import { SfkGatekeeperLogPage } from '../pages/sfk/SfkGatekeeperLogPage';
import { SfkRiskHeatmapPage } from '../pages/sfk/SfkRiskHeatmapPage';
import { SfkAuditPackPage } from '../pages/sfk/SfkAuditPackPage';
import {
  SfkDistributionModePage,
  SfkAppUserTypePage,
  SfkConnectionStatusPage,
  SfkOrgNodeTypePage,
  SfkRunTypePage,
  SfkTrackingTokenPage,
  SfkVarianceTypePage,
  SfkVarianceDispositionPage,
  SfkIntakeTypePage,
  SfkIntakeStatusPage,
  SfkIntakeReasonCodePage,
  SfkRuleSeverityPage,
  SfkRuleOpStatePage,
  SfkContinuityStatusPage,
  SfkViolationStatusPage,
  SfkReasonCodePage,
  SfkMitigatingActionCodePage,
  SfkSandboxStatusPage,
  SfkSandboxOperationPage,
  SfkSandboxDispositionPage,
  SfkTransactionStatusPage,
  SfkTransactionClosureReasonPage,
  SfkAlertStatusPage,
  SfkReleaseStatusPage,
  SfkReleaseItemStatusPage,
  SfkRegulatoryFrameworkPage,
  SfkGatekeeperDispositionPage,
} from '../pages/sfk/SfkLookupPages';
import {
  SfkReportUserAuthorization,
  SfkReportUserRoles,
  SfkReportRoleActions,
  SfkReportUserActions,
  SfkReportRoleUsers,
  SfkReportUserConflicts,
  SfkReportPolicyConflicts,
} from '../pages/sfk/reports/SfkReportPages';

export const APP_PAGE_COMPONENTS: Record<string, ComponentType<PageComponentProps>> = {
  'SFK.APPLICATION.LIST': SfkApplicationPage,
  'SFK.BUNDLE.LIST': SfkBundlePage,
  'SFK.ORG_NODE.LIST': SfkOrgNodePage,
  'SFK.INGESTION_RUN.LIST': SfkIngestionRunPage,
  'SFK.API_FIELD_MAP.LIST': SfkApiFieldMapPage,
  'SFK.VARIANCE.LIST': SfkVariancePage,
  'SFK.INTAKE_REQUEST.LIST': SfkIntakeRequestPage,
  'SFK.CONFLICT_RULE.LIST': SfkConflictRulePage,
  'SFK.SPOF_FINDING.LIST': SfkSpofFindingPage,
  'SFK.APP_USER_GROUP.LIST': SfkAppUserGroupPage,
  'SFK.VIOLATION.LIST': SfkViolationPage,
  'SFK.MITIGATION_TEMPLATE.LIST': SfkMitigationTemplatePage,
  'SFK.SANDBOX_SESSION.LIST': SfkSandboxSessionPage,
  'SFK.DELEGATION.LIST': SfkDelegationPage,
  'SFK.OPEN_TRANSACTION.LIST': SfkOpenTransactionPage,
  'SFK.ALERT.LIST': SfkAlertPage,
  'SFK.RELEASE.LIST': SfkReleasePage,
  'SFK.GATEKEEPER_LOG.LIST': SfkGatekeeperLogPage,
  'SFK.RISK_HEATMAP.LIST': SfkRiskHeatmapPage,
  'SFK.AUDIT_PACK.LIST': SfkAuditPackPage,
  'SFK.LOOKUP.DISTRIBUTION_MODE': SfkDistributionModePage,
  'SFK.LOOKUP.APP_USER_TYPE': SfkAppUserTypePage,
  'SFK.LOOKUP.CONNECTION_STATUS': SfkConnectionStatusPage,
  'SFK.LOOKUP.ORG_NODE_TYPE': SfkOrgNodeTypePage,
  'SFK.LOOKUP.RUN_TYPE': SfkRunTypePage,
  'SFK.LOOKUP.TRACKING_TOKEN': SfkTrackingTokenPage,
  'SFK.LOOKUP.VARIANCE_TYPE': SfkVarianceTypePage,
  'SFK.LOOKUP.VARIANCE_DISPOSITION': SfkVarianceDispositionPage,
  'SFK.LOOKUP.INTAKE_TYPE': SfkIntakeTypePage,
  'SFK.LOOKUP.INTAKE_STATUS': SfkIntakeStatusPage,
  'SFK.LOOKUP.INTAKE_REASON_CODE': SfkIntakeReasonCodePage,
  'SFK.LOOKUP.RULE_SEVERITY': SfkRuleSeverityPage,
  'SFK.LOOKUP.RULE_OP_STATE': SfkRuleOpStatePage,
  'SFK.LOOKUP.CONTINUITY_STATUS': SfkContinuityStatusPage,
  'SFK.LOOKUP.VIOLATION_STATUS': SfkViolationStatusPage,
  'SFK.LOOKUP.REASON_CODE': SfkReasonCodePage,
  'SFK.LOOKUP.MITIGATING_ACTION_CODE': SfkMitigatingActionCodePage,
  'SFK.LOOKUP.SANDBOX_STATUS': SfkSandboxStatusPage,
  'SFK.LOOKUP.SANDBOX_OPERATION': SfkSandboxOperationPage,
  'SFK.LOOKUP.SANDBOX_DISPOSITION': SfkSandboxDispositionPage,
  'SFK.LOOKUP.TRANSACTION_STATUS': SfkTransactionStatusPage,
  'SFK.LOOKUP.TRANSACTION_CLOSURE_REASON': SfkTransactionClosureReasonPage,
  'SFK.LOOKUP.ALERT_STATUS': SfkAlertStatusPage,
  'SFK.LOOKUP.RELEASE_STATUS': SfkReleaseStatusPage,
  'SFK.LOOKUP.RELEASE_ITEM_STATUS': SfkReleaseItemStatusPage,
  'SFK.LOOKUP.REGULATORY_FRAMEWORK': SfkRegulatoryFrameworkPage,
  'SFK.LOOKUP.GATEKEEPER_DISPOSITION': SfkGatekeeperDispositionPage,
  'SFK.REPORT.USER_AUTHORIZATION': SfkReportUserAuthorization,
  'SFK.REPORT.USER_ROLES': SfkReportUserRoles,
  'SFK.REPORT.ROLE_ACTIONS': SfkReportRoleActions,
  'SFK.REPORT.USER_ACTIONS': SfkReportUserActions,
  'SFK.REPORT.ROLE_USERS': SfkReportRoleUsers,
  'SFK.REPORT.USER_CONFLICTS': SfkReportUserConflicts,
  'SFK.REPORT.POLICY_CONFLICTS': SfkReportPolicyConflicts,
};
